/**
 * Dummy timesheet data for UI development.
 *
 * Remove this file when the timesheet API is live, and set
 * USE_DUMMY_TIMESHEET_DATA = false in utils/employeeService.js
 */

import { PAYMENT_STATUS } from "./timesheetUtils";

const hoursAgo = (hours) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
const daysAgoAt = (days, hour, minute = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
};

export const DUMMY_EMPLOYEES = [
  {
    id: "dummy-emp-1",
    name: "Priya Sharma",
    email: "priya.sharma@kothar.edu.au",
    contact: "0412 334 221",
    pin: "1234",
    startDate: "2024-03-11T00:00:00.000Z",
    jobTitle: "Admissions Officer",
    department: "Admissions",
    status: "ACTIVE",
    employmentType: "FULL_TIME",
    payType: "MONTHLY",
    monthlySalary: 45000,
    expectedHoursPerMonth: 208,
    hourlyRate: null,
    overtimeEligible: true,
    isFixedHours: true,
    fixedHours: "208",
    role: "EMPLOYEE",
    isClockedIn: true,
    clockedIn: true,
    orgId: null,
    orgName: "",
  },
  {
    id: "dummy-emp-2",
    name: "James Wilson",
    email: "james.wilson@kothar.edu.au",
    contact: "0438 901 774",
    pin: "5678",
    startDate: "2023-08-01T00:00:00.000Z",
    jobTitle: "Accounts Coordinator",
    department: "Accounts",
    status: "ACTIVE",
    employmentType: "FULL_TIME",
    payType: "MONTHLY",
    monthlySalary: 55000,
    expectedHoursPerMonth: 208,
    hourlyRate: null,
    overtimeEligible: false,
    isFixedHours: true,
    fixedHours: "208",
    role: "EMPLOYEE",
    isClockedIn: false,
    clockedIn: false,
    orgId: null,
    orgName: "",
  },
];

export let DUMMY_TIMESHEETS = [
  {
    id: "dummy-ts-1",
    employeeId: "dummy-emp-1",
    employeeName: "Priya Sharma",
    clockIn: hoursAgo(3.2),
    clockOut: null,
    durationMinutes: 0,
    breakMinutes: 0,
    paymentStatus: PAYMENT_STATUS.UNPAID,
  },
  {
    id: "dummy-ts-2",
    employeeId: "dummy-emp-1",
    employeeName: "Priya Sharma",
    clockIn: daysAgoAt(1, 9, 5),
    clockOut: daysAgoAt(1, 17, 12),
    durationMinutes: 487,
    breakMinutes: 30,
    paymentStatus: PAYMENT_STATUS.UNPAID,
  },
  {
    id: "dummy-ts-3",
    employeeId: "dummy-emp-1",
    employeeName: "Priya Sharma",
    clockIn: daysAgoAt(2, 8, 55),
    clockOut: daysAgoAt(2, 16, 40),
    durationMinutes: 465,
    breakMinutes: 45,
    paymentStatus: PAYMENT_STATUS.PAID,
    paymentMethod: "BANK",
    paidOn: daysAgoAt(2, 18, 0).slice(0, 10),
    paymentNote: "Salary transfer",
  },
  {
    id: "dummy-ts-4",
    employeeId: "dummy-emp-2",
    employeeName: "James Wilson",
    clockIn: daysAgoAt(0, 8, 30),
    clockOut: daysAgoAt(0, 12, 0),
    durationMinutes: 210,
    breakMinutes: 15,
    paymentStatus: PAYMENT_STATUS.UNPAID,
  },
  {
    id: "dummy-ts-5",
    employeeId: "dummy-emp-2",
    employeeName: "James Wilson",
    clockIn: daysAgoAt(3, 9, 0),
    clockOut: daysAgoAt(3, 17, 30),
    durationMinutes: 510,
    breakMinutes: 60,
    paymentStatus: PAYMENT_STATUS.PAID,
    paymentMethod: "CASH",
    paidOn: daysAgoAt(3, 18, 0).slice(0, 10),
    paymentNote: "",
  },
];

const clone = (value) => JSON.parse(JSON.stringify(value));

export const getDummyEmployees = () => clone(DUMMY_EMPLOYEES);

export const getDummyEmployeeById = (id) =>
  clone(DUMMY_EMPLOYEES.find((employee) => String(employee.id) === String(id)) || null);

export const getDummyTimesheets = (params = {}) => {
  let content = clone(DUMMY_TIMESHEETS);
  if (params.employeeId) {
    content = content.filter((entry) => String(entry.employeeId) === String(params.employeeId));
  }
  if (params.paymentStatus) {
    content = content.filter((entry) => entry.paymentStatus === params.paymentStatus);
  }
  content.sort((a, b) => new Date(b.clockIn) - new Date(a.clockIn));
  return { content, totalItems: content.length, totalPages: 1 };
};

export const dummyVerifyPin = (pin) => {
  const employee = DUMMY_EMPLOYEES.find((item) => String(item.pin) === String(pin).trim());
  if (!employee) {
    const error = new Error("Invalid PIN");
    error.response = { data: { errorMessage: "Invalid PIN. Try 1234 or 5678." } };
    throw error;
  }
  return clone(employee);
};

export const dummyClockStatus = (employeeId) => {
  const employee = DUMMY_EMPLOYEES.find((item) => String(item.id) === String(employeeId));
  const openEntry = DUMMY_TIMESHEETS.find(
    (entry) => String(entry.employeeId) === String(employeeId) && !entry.clockOut
  );
  const lastClockIn = openEntry?.clockIn || null;
  return {
    isClockedIn: Boolean(employee?.isClockedIn || openEntry),
    lastClockIn,
    lastClockOut:
      DUMMY_TIMESHEETS.find(
        (entry) => String(entry.employeeId) === String(employeeId) && entry.clockOut
      )?.clockOut || null,
  };
};

export const dummyClockIn = (employeeId) => {
  const employee = DUMMY_EMPLOYEES.find((item) => String(item.id) === String(employeeId));
  if (!employee) throw new Error("Employee not found");
  const alreadyOpen = DUMMY_TIMESHEETS.some(
    (entry) => String(entry.employeeId) === String(employeeId) && !entry.clockOut
  );
  if (alreadyOpen) {
    const error = new Error("Already clocked in");
    error.response = { data: { errorMessage: "Already clocked in" } };
    throw error;
  }
  employee.isClockedIn = true;
  employee.clockedIn = true;
  const entry = {
    id: `dummy-ts-${Date.now()}`,
    employeeId: employee.id,
    employeeName: employee.name,
    clockIn: new Date().toISOString(),
    clockOut: null,
    durationMinutes: 0,
    breakMinutes: 0,
    paymentStatus: PAYMENT_STATUS.UNPAID,
  };
  DUMMY_TIMESHEETS = [entry, ...DUMMY_TIMESHEETS];
  return clone(entry);
};

export const dummyClockOut = (employeeId) => {
  const employee = DUMMY_EMPLOYEES.find((item) => String(item.id) === String(employeeId));
  const openEntry = DUMMY_TIMESHEETS.find(
    (entry) => String(entry.employeeId) === String(employeeId) && !entry.clockOut
  );
  if (!openEntry) {
    const error = new Error("Not clocked in");
    error.response = { data: { errorMessage: "Not clocked in" } };
    throw error;
  }
  openEntry.clockOut = new Date().toISOString();
  openEntry.durationMinutes = Math.max(
    1,
    Math.round((new Date(openEntry.clockOut) - new Date(openEntry.clockIn)) / 60000)
  );
  if (employee) {
    employee.isClockedIn = false;
    employee.clockedIn = false;
  }
  return clone(openEntry);
};

export const dummyUpdatePayment = ({ id, paymentStatus, paymentMethod, paidOn, paymentNote }) => {
  const entry = DUMMY_TIMESHEETS.find((item) => String(item.id) === String(id));
  if (!entry) throw new Error("Timesheet entry not found");
  entry.paymentStatus = paymentStatus;
  if (paymentStatus === PAYMENT_STATUS.PAID) {
    entry.paymentMethod = paymentMethod || entry.paymentMethod || "CASH";
    entry.paidOn = paidOn || entry.paidOn || new Date().toISOString().slice(0, 10);
    entry.paymentNote = paymentNote ?? entry.paymentNote ?? "";
  } else {
    entry.paymentMethod = null;
    entry.paidOn = null;
    entry.paymentNote = "";
  }
  return clone(entry);
};

export const dummyMarkTimesheetsPaid = ({ ids = [], paymentMethod, paidOn, note }) => {
  return (ids || []).map((id) =>
    dummyUpdatePayment({
      id,
      paymentStatus: PAYMENT_STATUS.PAID,
      paymentMethod,
      paidOn,
      paymentNote: note,
    })
  );
};

export const dummyCreateEmployee = (employeeData) => {
  const employee = {
    id: `dummy-emp-${Date.now()}`,
    isClockedIn: false,
    clockedIn: false,
    ...employeeData,
  };
  DUMMY_EMPLOYEES.push(employee);
  return clone(employee);
};

export const dummyUpdateEmployee = (id, employeeData) => {
  const index = DUMMY_EMPLOYEES.findIndex((item) => String(item.id) === String(id));
  if (index < 0) throw new Error("Employee not found");
  DUMMY_EMPLOYEES[index] = { ...DUMMY_EMPLOYEES[index], ...employeeData, id };
  return clone(DUMMY_EMPLOYEES[index]);
};

export const dummyAdjustTimesheet = ({ id, clockIn, clockOut, breakMinutes }) => {
  const entry = DUMMY_TIMESHEETS.find((item) => String(item.id) === String(id));
  if (!entry) throw new Error("Timesheet entry not found");
  if (clockIn) entry.clockIn = new Date(clockIn).toISOString();
  if (clockOut) {
    entry.clockOut = new Date(clockOut).toISOString();
  } else if (clockOut === "" || clockOut === null) {
    entry.clockOut = null;
  }
  if (breakMinutes != null && breakMinutes !== "") {
    entry.breakMinutes = Math.max(0, Number(breakMinutes) || 0);
  }
  if (entry.clockIn && entry.clockOut) {
    entry.durationMinutes = Math.max(
      0,
      Math.round((new Date(entry.clockOut) - new Date(entry.clockIn)) / 60000)
    );
  } else {
    entry.durationMinutes = 0;
  }
  return clone(entry);
};
